import React, { useEffect, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router";
import authService from "src/services/authService";
import { useSelector } from "src/store";
import { selectProfile } from "src/store/account";
import { selectHasValidSession } from "src/store/auth";
import { CircularProgressBox } from "src/ui/shared/CircularProgressBox";

import { TechnicalDifficultiesFallback } from "@shared/TechnicalDifficultiesFallback";

export const AuthGuard = () => {
    const location = useLocation();
    const hasValidSession = useSelector(selectHasValidSession);
    const profile = useSelector(selectProfile);
    const [hasAuthError, setHasAuthError] = useState(false);

    useEffect(() => {
        if (!hasValidSession) {
            // AuthGuard kicks off the OAuth redirect to the IdP authorization endpoint,
            // which bounces back to /login with client_id/redirect_uri/state in the query string.
            void authService.initiateAuthFlow(location.pathname + location.search).catch(() => setHasAuthError(true));
        }
    }, [hasValidSession, location.pathname, location.search]);

    if (hasAuthError) {
        return <TechnicalDifficultiesFallback />;
    }

    if (!hasValidSession) {
        // No valid session on a protected route: the effect above already kicked off the OAuth
        // redirect to the IdP (a full-page window.location.href navigation), but that is async.
        // Hold this neutral placeholder so the protected subtree never mounts during the redirect
        // window (admin/* routes sit outside PrefetchGuard and would otherwise leak here).
        return <CircularProgressBox size="large" />;
    }

    if (profile && !profile.profileCompleted) {
        return <Navigate to="/account/confirm" replace />;
    }

    return <Outlet />;
};
